import { useEffect, useRef, useState } from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import {
  FORMAT,
  formatDate,
  getDatesBetween,
} from '../../../../../../lib/date';
import request from '../../../../../../lib/request';
import Routine from './routine';
import MenuBottomDialog from './menuBottomDialog';
import LogBottomDialog from './logBottomDialog';
import WorkoutBottomDialog from './workoutBottomDialog';
import Info from './info';
import DayList from './dayList';
import RestDayBottomDialog from './restDayBottomDialog';

const Daily = ({ challenge, routines, refresh, closeDetailDialog, status }) => {
  const dialogRef = useRef(null);
  const workoutDialogRef = useRef(null);
  const logDialogRef = useRef(null);
  const workoutBottomDialogRef = useRef(null);
  const restDayBottomDialogRef = useRef(null);
  const infoBottomDialogRef = useRef(null);

  const [days, setDays] = useState([]);
  const [log, setLog] = useState([]);
  const [habitLog, setHabitLog] = useState([]);
  const [selectedWorkout, setSelectedWorkout] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(null);
  const [isSelectedWorkoutDone, setIsSelectedWorkoutDone] = useState(false);
  const [isWorkoutBottomDialogOpen, setIsWorkoutBottomDialogOpen] =
    useState(false);
  const [isWorkoutDialogOpen, setIsWorkoutDialogOpen] = useState(false);
  const [selectedDayNumber, setSelectedDayNumber] = useState(0);

  useEffect(() => {
    if (challenge.id) {
      const days = getDatesBetween(challenge.startDate, challenge.endDate);
      setDays(days);
      getTrackers();
      getHabitLog();
    }
  }, [challenge]);

  const getTrackers = async () => {
    try {
      const { result } = await request.get(
        `/workout/challenge/${challenge.id}/log`
      );
      if (result?.length > 0) {
        setLog(result);
      } else {
        setLog([]);
      }

      return result;
    } catch (err) {
      console.log(err);
    }
  };

  const getHabitLog = async () => {
    try {
      const { result } = await request.get(
        `/habit/log?userChallengeId=${challenge.id}`
      );
      if (result?.length > 0) {
        setHabitLog(result);
      } else {
        setHabitLog([]);
      }

      return result;
    } catch (err) {
      console.log(err);
    }
  };

  const openBottomDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.showModal();
    }
  };

  const closeBottomDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const openWorkoutDialog = () => {
    if (workoutDialogRef.current) {
      workoutDialogRef.current.showModal();
      setIsWorkoutDialogOpen(true);
    }
  };

  const closeWorkoutDialog = () => {
    if (workoutDialogRef.current) {
      workoutDialogRef.current.close();
      setIsWorkoutDialogOpen(false);
    }
  };

  const openLogDialog = () => {
    if (logDialogRef.current) {
      logDialogRef.current.showModal();
    }
  };

  const closeLogDialog = () => {
    if (logDialogRef.current) {
      logDialogRef.current.close();
    }
  };

  const openWorkoutBottomDialog = () => {
    if (workoutBottomDialogRef.current) {
      workoutBottomDialogRef.current.showModal();
      setIsWorkoutBottomDialogOpen(true);
    }
  };

  const closeWorkoutBottomDialog = () => {
    if (workoutBottomDialogRef.current) {
      workoutBottomDialogRef.current.close();
      setIsWorkoutBottomDialogOpen(false);
    }
  };

  const openRestDayBottomDialog = () => {
    if (restDayBottomDialogRef.current) {
      restDayBottomDialogRef.current.showModal();
    }
  };

  const closeRestDayBottomDialog = () => {
    if (restDayBottomDialogRef.current) {
      restDayBottomDialogRef.current.close();
    }
  };

  const openInfoBottomDialog = () => {
    if (infoBottomDialogRef.current) {
      infoBottomDialogRef.current.showModal();
    }
  };

  const closeInfoBottomDialog = () => {
    if (infoBottomDialogRef.current) {
      infoBottomDialogRef.current.close();
    }
  };

  const updateStatus = (status) => async () => {
    try {
      if (
        window.confirm(
          `Do you want to ${
            status === 'COMPLETE' ? 'complete' : 'give up'
          } the challenge?`
        )
      ) {
        const { result } = await request.put(
          `/user/challenge/${challenge.id}/status`,
          { status }
        );
        if (result) {
          closeBottomDialog();
          closeDetailDialog();
          refresh();
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createHabitLog = async () => {
    try {
      if (window.confirm("Did you finish today's action?")) {
        if (
          selectedDayIndex !== null &&
          habitLog.every((item) => item.logDate !== selectedDay)
        ) {
          await request.post(`/habit/record`, {
            logDate: selectedDay,
            logTime: formatDate(new Date(), FORMAT.HOURMIN),
            challengeId: challenge.challengeId,
            userChallengeId: challenge.id,
          });
          const updatedLogs = await getHabitLog();

          if (updatedLogs.length === days.length) {
            await request.put(`/user/challenge/${challenge.id}/status`, {
              status: 'COMPLETE',
            });
          }
        }
        closeLogDialog();
        setSelectedDay(null);
        setSelectedDayIndex(null);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSelectDay = (day, index) => () => {
    setSelectedDay(formatDate(day));
    setSelectedDayIndex(index);
    setSelectedDayNumber(index + 1);

    if (challenge.category === 'HABIT') {
      openLogDialog();
    } else if (status === 'CURRENT' || status === 'HISTORY') {
      const dayRoutine = routines.find((item) => item.dayNumber === index + 1);

      const isDoneForTheDay = log.some(
        (item) => item.logDate === formatDate(day)
      );
      setIsSelectedWorkoutDone(isDoneForTheDay);

      if (dayRoutine) {
        setSelectedWorkout(dayRoutine);
        openWorkoutBottomDialog();
      } else {
        openRestDayBottomDialog();
      }
    }
  };

  const startWorkout = () => {
    if (
      selectedDayIndex === null ||
      log.some((item) => item.logDate === selectedDay)
    ) {
      alert("Today's workout is done.");
      return;
    }

    const date = new Date(selectedDay);
    date.setHours(0, 0, 0, 0);

    if (date > new Date()) {
      alert("It's not for today.");
      closeWorkoutBottomDialog();
      return;
    }

    if (selectedWorkout) {
      openWorkoutDialog();
    }
  };

  const takeRest = async () => {
    try {
      if (
        selectedDayIndex === null ||
        log.some(
          (item) => item.logDate === selectedDay && item.workout === 'REST'
        )
      ) {
        alert("Today's workout is done.");
        return;
      }

      const date = new Date(selectedDay);
      date.setHours(0, 0, 0, 0);

      if (date > new Date()) {
        alert("It's not for today.");
        return;
      }

      const data = {
        workout: 'REST',
        userChallengeId: challenge.id,
        logDate: selectedDay,
        logTime: formatDate(new Date(), FORMAT.HOURMIN),
        type: 'CHALLENGE',
      };

      const { result } = await request.post(`/workout/record`, data);
      if (!result) {
        return alert('Fail to record');
      }

      closeRestDayBottomDialog();
      await getTrackers();
    } catch (err) {
      console.log(err);
    }
  };

  const getDistinctLogCount = () => {
    const targetLog = challenge.category === 'HABIT' ? habitLog : log;
    return new Set(targetLog.map((item) => item.logDate)).size;
  };

  return (
    <div>
      <div className='relative'>
        <img
          src={challenge.bannerUrl}
          alt='challenge'
          className='h-36 w-full rounded-t-xl object-cover object-center'
        />
        <div className='absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-t from-white rounded-t-xl'></div>
        <div className='absolute top-2 right-3 flex items-center gap-4'>
          <div
            className='border-2 rounded-full size-5 flex justify-center items-center text-white font-bold'
            onClick={openInfoBottomDialog}
          >
            i
          </div>
          {challenge.status === 'ONGOING' && (
            <EllipsisHorizontalIcon
              className='text-gray-200 size-6'
              onClick={openBottomDialog}
            />
          )}
        </div>
      </div>
      <div className='mt-4 mb-8'>
        <div className='flex items-center justify-between'>
          <h2>
            {getDistinctLogCount()} / {challenge.days} Days Completed
          </h2>
          <div>
            {Math.floor((getDistinctLogCount() / challenge.days) * 100)}%
          </div>
        </div>
        <div className='bg-gray-200 rounded-full h-2'>
          <div
            className='bg-black h-2 rounded-full transition-width duration-500'
            style={{
              width: `${(getDistinctLogCount() / challenge.days) * 100}%`,
            }}
          ></div>
        </div>
      </div>
      <DayList
        days={days}
        log={challenge?.category === 'HABIT' ? habitLog : log}
        challenge={challenge}
        routines={routines}
        handleSelectDay={handleSelectDay}
      />
      <MenuBottomDialog
        dialogRef={dialogRef}
        closeBottomDialog={closeBottomDialog}
        updateStatus={updateStatus}
      />
      <LogBottomDialog
        dialogRef={logDialogRef}
        closeBottomDialog={closeLogDialog}
        createLog={createHabitLog}
      />
      <WorkoutBottomDialog
        open={isWorkoutBottomDialogOpen}
        dialogRef={workoutBottomDialogRef}
        closeBottomDialog={closeWorkoutBottomDialog}
        startWorkout={startWorkout}
        isSelectedWorkoutDone={isSelectedWorkoutDone}
        challenge={challenge}
        selectedWorkout={selectedWorkout}
        selectedDayNumber={selectedDayNumber}
        selectedDay={selectedDay}
      />
      <RestDayBottomDialog
        dialogRef={restDayBottomDialogRef}
        closeBottomDialog={closeRestDayBottomDialog}
        takeRest={takeRest}
        isSelectedWorkoutDone={isSelectedWorkoutDone}
        challenge={challenge}
        selectedWorkout={selectedWorkout}
        selectedDay={selectedDay}
      />
      <Info
        dialogRef={infoBottomDialogRef}
        closeBottomDialog={closeInfoBottomDialog}
        challenge={challenge}
      />
      {(challenge.status === 'ONGOING' || challenge.status === 'INCOMPLETE') &&
        selectedWorkout.id && (
          <Routine
            challenge={challenge}
            selectedRoutine={selectedWorkout}
            dialogRef={workoutDialogRef}
            closeDialog={closeWorkoutDialog}
            getTrackers={getTrackers}
            open={isWorkoutDialogOpen}
            selectedDay={selectedDay}
            closeWorkoutBottomDialog={closeWorkoutBottomDialog}
          />
        )}
    </div>
  );
};

export default Daily;
